import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2, Save, Layers, X } from "lucide-react";
import { toast } from "sonner";

export default function CatalogManager() {
  const [tree, setTree] = useState([]);
  const [newSistema, setNewSistema] = useState("");
  const [saving, setSaving] = useState(false);

  const uid = () => Math.random().toString(36).slice(2, 10);
  const withKeys = (sistemas) => (sistemas || []).map((s) => ({
    ...s, _k: uid(), modulos: (s.modulos || []).map((m) => ({ ...m, _k: uid() })),
  }));

  useEffect(() => {
    api.get("/catalogs").then((r) => setTree(withKeys(r.data.sistemas || [])));
  }, []);

  const clone = (t) => JSON.parse(JSON.stringify(t));

  const addSistema = () => {
    const v = newSistema.trim();
    if (!v) return;
    setTree((t) => [...t, { _k: uid(), name: v, modulos: [] }]);
    setNewSistema("");
  };
  const renameSistema = (i, v) => setTree((t) => { const n = clone(t); n[i].name = v; return n; });
  const delSistema = (i) => setTree((t) => t.filter((_, idx) => idx !== i));

  const addModulo = (si, name) => {
    const v = (name || "").trim();
    if (!v) return;
    setTree((t) => { const n = clone(t); n[si].modulos.push({ _k: uid(), name: v, submodulos: [] }); return n; });
  };
  const renameModulo = (si, mi, v) => setTree((t) => { const n = clone(t); n[si].modulos[mi].name = v; return n; });
  const delModulo = (si, mi) => setTree((t) => { const n = clone(t); n[si].modulos.splice(mi, 1); return n; });

  const addSub = (si, mi, name) => {
    const v = (name || "").trim();
    if (!v) return;
    setTree((t) => { const n = clone(t); n[si].modulos[mi].submodulos.push(v); return n; });
  };
  const delSub = (si, mi, ki) => setTree((t) => { const n = clone(t); n[si].modulos[mi].submodulos.splice(ki, 1); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/catalogs/tree", { sistemas: tree });
      setTree(withKeys(data.sistemas || []));
      toast.success("Catálogo actualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-white rounded-sm p-6" data-testid="catalog-manager">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Layers className="h-5 w-5" />Catálogo de Sistemas</h3>
        <Button data-testid="save-catalog-btn" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Administra sistemas, módulos y submódulos disponibles al crear desarrollos.</p>

      <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
        {tree.map((s, si) => (
          <div key={s._k} className="border border-slate-200 rounded-sm">
            <div className="flex items-center gap-2 bg-slate-50 border-b border-slate-200 px-3 py-2">
              <Input value={s.name} onChange={(e) => renameSistema(si, e.target.value)} className="rounded-sm h-8 font-semibold" data-testid={`sistema-name-${si}`} />
              <button onClick={() => delSistema(si)} className="text-slate-400 hover:text-red-600 transition-colors duration-200 shrink-0" data-testid={`del-sistema-${si}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              {s.modulos.map((m, mi) => (
                <div key={m._k} className="border border-slate-100 rounded-sm p-2">
                  <div className="flex items-center gap-2">
                    <Input value={m.name} onChange={(e) => renameModulo(si, mi, e.target.value)} className="rounded-sm h-8 text-sm" />
                    <button onClick={() => delModulo(si, mi)} className="text-slate-400 hover:text-red-600 transition-colors duration-200 shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 pl-1">
                    {m.submodulos.map((sm, ki) => (
                      <span key={sm} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-sm px-2 py-0.5 text-xs">
                        {sm}
                        <button onClick={() => delSub(si, mi, ki)} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                    <SubInput onAdd={(v) => addSub(si, mi, v)} />
                  </div>
                </div>
              ))}
              <ModuloInput onAdd={(v) => addModulo(si, v)} />
            </div>
          </div>
        ))}

        <div className="flex items-center gap-2">
          <Input value={newSistema} onChange={(e) => setNewSistema(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSistema(); } }}
            placeholder="Nuevo sistema..." className="rounded-sm h-9" data-testid="new-sistema-input" />
          <Button onClick={addSistema} variant="outline" className="rounded-sm h-9" data-testid="add-sistema-btn"><Plus className="h-4 w-4 mr-1" />Sistema</Button>
        </div>
      </div>
    </div>
  );
}

function ModuloInput({ onAdd }) {
  const [v, setV] = useState("");
  const go = () => { onAdd(v); setV(""); };
  return (
    <div className="flex items-center gap-2">
      <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
        placeholder="Nuevo módulo..." className="rounded-sm h-8 text-sm" />
      <button onClick={go} className="h-8 px-2 flex items-center bg-slate-100 hover:bg-slate-200 rounded-sm transition-colors duration-200 text-xs font-semibold shrink-0"><Plus className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function SubInput({ onAdd }) {
  const [v, setV] = useState("");
  const go = () => { onAdd(v); setV(""); };
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
      placeholder="+ submódulo" className="border border-dashed border-slate-300 rounded-sm px-2 py-0.5 text-xs w-28 focus:outline-none focus:border-slate-900" />
  );
}
